import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MailercloudLogo from '../MailercloudLogo';
import { useUser } from '../../contexts/UserContext';
import './Sidebar.css';

interface SidebarProps {
  onSignOut: () => void;
  onSidebarStateChange?: (isExpanded: boolean) => void;
}

interface NavigationItem {
  label: string;
  path: string;
  icon: string;
  isActive: boolean;
  action?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ onSignOut, onSidebarStateChange }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();

  // Check if screen is mobile size
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsExpanded(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Notify parent component when sidebar state changes
  useEffect(() => {
    if (onSidebarStateChange && !isMobile) {
      onSidebarStateChange(isExpanded);
    }
  }, [isExpanded, isMobile, onSidebarStateChange]);

  const toggleSidebar = () => {
    if (isMobile) {
      setShowMobileMenu(!showMobileMenu);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  const navigateTo = (path: string, action?: string) => {
    if (action === 'addCustomer') {
      // Navigate directly to the add customer page
      navigate('/add-customer');
    } else {
      navigate(path);
    }
    
    if (isMobile) {
      setShowMobileMenu(false);
    }
  };

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    return user.name
      .split(' ')
      .map(word => word.charAt(0))
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const navigationItems: NavigationItem[] = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: '⚡',
      isActive: location.pathname === '/dashboard'
    },
    {
      label: 'Add Customer',
      path: '/add-customer',
      icon: '👤',
      isActive: location.pathname === '/add-customer',
      action: 'addCustomer'
    },
    {
      label: 'Price Calculator',
      path: '/price-calculator',
      icon: '💰',
      isActive: location.pathname === '/price-calculator'
    },
    {
      label: 'Transactional Pricing',
      path: '/transactional-price-calculator',
      icon: '📧',
      isActive: location.pathname === '/transactional-price-calculator'
    },
    {
      label: 'Performance Report',
      path: '/client-performance',
      icon: '📊',
      isActive: location.pathname === '/client-performance'
    }
  ];

  const sidebarClass = `sidebar ${isExpanded ? 'expanded' : 'collapsed'} ${isMobile && showMobileMenu ? 'mobile-open' : ''}`;

  return (
    <>
      {/* Mobile menu button */}
      {isMobile && (
        <button
          className="mobile-menu-toggle"
          onClick={toggleSidebar}
          aria-label="Toggle navigation menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
      )}

      {/* Mobile backdrop */}
      {isMobile && showMobileMenu && (
        <div 
          className="mobile-backdrop"
          onClick={() => setShowMobileMenu(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={sidebarClass} role="navigation" aria-label="Main navigation">
        {/* Header with logo */}
        <div className="sidebar-header">
          <div className="logo-container">
            <MailercloudLogo 
              size={isExpanded ? "md" : "sm"} 
              variant={isExpanded ? "horizontal" : "icon"} 
            />
          </div>
          {!isMobile && (
            <button
              className="sidebar-toggle"
              onClick={toggleSidebar}
              aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
              title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
            >
              <span className={`toggle-icon ${isExpanded ? 'expanded' : 'collapsed'}`}>
                ←
              </span>
            </button>
          )}
        </div>

        {/* Navigation items */}
        <nav className="sidebar-nav">
          <ul className="nav-list" role="menu">
            {navigationItems.map((item) => (
              <li key={item.path} className="nav-item" role="none">
                <button
                  className={`nav-link ${item.isActive ? 'active' : ''}`}
                  onClick={() => navigateTo(item.path, item.action)}
                  aria-current={item.isActive ? 'page' : undefined}
                  role="menuitem"
                  title={!isExpanded ? item.label : undefined}
                >
                  <span className="nav-icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  {isExpanded && (
                    <span className="nav-label">{item.label}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User section */}
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={`${user.name}'s profile`}
                  className="avatar-image"
                />
              ) : (
                <div className="avatar-placeholder">
                  {getUserInitials()}
                </div>
              )}
            </div>
            {isExpanded && (
              <div className="user-details">
                <div className="user-name">{user?.name || 'User'}</div>
                <div className="user-email">{user?.email || 'user@example.com'}</div>
              </div>
            )}
          </div>
          
          <button
            className="sign-out-button"
            onClick={onSignOut}
            aria-label="Sign out"
            title="Sign out"
          >
            <span className="sign-out-icon" aria-hidden="true">↗</span>
            {isExpanded && <span className="sign-out-label">Sign Out</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;